import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export const useCredentialsAuth = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const navigate = useNavigate();

  const loginWithCredentials = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      navigate("/admin");
      return true;
    } catch (error: any) {
      toast.error(error.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const signUpWithCredentials = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
        error: signUpError,
      } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: email.split("@")[0],
            company_name: companyName,
          },
        },
      });

      if (signUpError) throw signUpError;

      if (!user) {
        throw new Error("Failed to create user");
      }

      // Create default statuses for the company
      const defaultStatuses = [
        { name: "In Review", color: "#94a3b8", description: "", position: 0 },
        { name: "Planned", color: "#60a5fa", description: "", position: 1 },
        {
          name: "In Progress",
          color: "#f59e0b",
          description: "",
          position: 2,
        },
        { name: "Completed", color: "#10b981", description: "", position: 3 },
        { name: "Rejected", color: "#ef4444", description: "", position: 4 },
      ];

      // Get the company ID from the user's profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("id", user.id)
        .single();

      if (profile?.company_id) {
        // Check for Whop license and update company plan to enterprise if found
        const whopLicenseKey = localStorage.getItem('whop_license_key');
        const whopMembershipData = localStorage.getItem('whop_membership_data');

        if (whopLicenseKey && whopMembershipData) {
          try {
            console.log("Applying Whop license to company...");
            const membershipData = JSON.parse(whopMembershipData);

            // Update company to enterprise plan with license info
            const { error: updateCompanyError } = await supabase
              .from("companies")
              .update({
                plan: 'enterprise',
                whop_license_key: whopLicenseKey,
                whop_membership_id: membershipData.id,
                whop_validated_at: new Date().toISOString(),
                whop_metadata: membershipData,
              })
              .eq("id", profile.company_id);

            if (updateCompanyError) {
              console.error("Error updating company with license:", updateCompanyError);
              toast.error("Failed to apply license to company");
            } else {
              console.log("Company updated with Whop license successfully");

              // Bind the license with metadata
              const { error: bindError } = await supabase.functions.invoke('validate-whop-license', {
                body: {
                  licenseKey: whopLicenseKey,
                  companyId: profile.company_id,
                  preSignupValidation: false,
                },
              });

              if (bindError) {
                console.error("Error binding license:", bindError);
              } else {
                console.log("License bound to company successfully");
              }

              // Clean up localStorage
              localStorage.removeItem('whop_license_key');
              localStorage.removeItem('whop_membership_data');

              toast.success("Enterprise license activated!");
            }
          } catch (error) {
            console.error("Error processing Whop license:", error);
          }
        }

        // Create default statuses for the company
        const { error: statusError } = await supabase
          .from("company_statuses")
          .insert(
            defaultStatuses.map((status) => ({
              ...status,
              company_id: profile.company_id,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              position: status.position,
            }))
          );

        if (statusError) {
          console.error("Error creating default statuses:", statusError);
        } else {
          console.log("Default statuses created successfully");
        }

        // Check if user is already a team member (by user_id OR email)
        const { data: existingMember, error: lookupError } = await supabase
          .from("team_members")
          .select("id, user_id")
          .eq("company_id", profile.company_id)
          .or(`user_id.eq.${user.id},email.eq.${user.email?.toLowerCase()}`)
          .maybeSingle();

        if (lookupError) {
          console.error("Error checking for existing team member:", lookupError);
        } else if (existingMember) {
          // If a team member record exists but doesn't have user_id, update it
          if (!existingMember.user_id) {
            console.log("Updating existing team member record with user_id");
            const { error: updateError } = await supabase
              .from("team_members")
              .update({
                user_id: user.id,
                name: email.split("@")[0],
                status: 'active',
                updated_at: new Date().toISOString()
              })
              .eq("id", existingMember.id);

            if (updateError) {
              console.error("Error updating team member:", updateError);
            } else {
              console.log("Team member record updated successfully");
            }
          } else {
            console.log("User is already a team member, skipping duplicate entry");
          }
        } else {
          // No existing record, create a new one (owner scenario)
          const { error: teamMemberError } = await supabase
            .from("team_members")
            .insert({
              company_id: profile.company_id,
              email: user.email?.toLowerCase(),
              name: email.split("@")[0],
              user_id: user.id,
              status: 'active',
              invited_by: user.id,
              created_at: new Date().toISOString()
            });

          if (teamMemberError) {
            console.error("Error adding user to team members:", teamMemberError);
          } else {
            console.log("User added to team members successfully");
          }
        }
      }

      // Track Google Ads conversion for signup
      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }

      navigate("/admin");
      return user;
    } catch (error: any) {
      toast.error(error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    email,
    setEmail,
    password,
    setPassword,
    companyName,
    setCompanyName,
    isLogin,
    setIsLogin,
    loginWithCredentials,
    signUpWithCredentials,
  };
};
